<template>
  <div class="compare-mode-container">
    <div class="page-header">
      <h2>Compare Mode</h2>
      <p>Upload two videos and sync them by setting gate times (best on desktop)</p>
      <div class="feature-credit">Suggested by Geraldine Jamin</div>
    </div>

    <!-- Create New Comparison -->
    <div v-if="!currentComparison" class="create-comparison">
      <div class="form-group">
        <label>Comparison Name</label>
        <input 
          v-model="newComparisonName"
          type="text" 
          placeholder="Enter a name for this comparison"
          class="form-input"
        />
      </div>
      <button @click="createComparison" class="action-button primary" :disabled="!newComparisonName.trim()">
        Create Comparison
      </button>
    </div>

    <!-- Existing Comparisons List -->
    <div v-if="comparisons.length > 0 && !currentComparison" class="comparisons-list">
      <h3>Your Comparisons</h3>
      <div class="comparison-card" v-for="comparison in comparisons" :key="comparison.id">
        <div class="comparison-info">
          <h4>{{ comparison.name }}</h4>
          <p>Created: {{ formatDate(comparison.created) }}</p>
          <div class="video-status">
            <span class="status-badge" :class="{ 'complete': comparison.video1 }">
              Video 1: {{ comparison.video1 ? 'Uploaded' : 'Missing' }}
            </span>
            <span class="status-badge" :class="{ 'complete': comparison.video2 }">
              Video 2: {{ comparison.video2 ? 'Uploaded' : 'Missing' }}
            </span>
          </div>
        </div>
        <div class="comparison-actions">
          <button @click="selectComparison(comparison)" class="action-button">Open</button>
          <button @click="deleteComparison(comparison.id)" class="action-button danger">Delete</button>
        </div>
      </div>
    </div>

    <!-- Current Comparison Interface -->
    <div v-if="currentComparison" class="comparison-interface" :data-synced="syncConfirmed">
      <div class="comparison-header">
        <h3>{{ currentComparison.name }}</h3>
        <button @click="backToList" class="action-button secondary">Back to List</button>
      </div>

      <!-- Video Upload Section -->
      <div class="video-upload-section">
        <div class="video-upload-card">
          <h4 v-if="!syncConfirmed">Video 1</h4>
          <div v-if="!currentComparison.video1" class="upload-area">
            <input 
              type="file" 
              ref="video1Input"
              @change="handleVideoSelect($event, 1)"
              accept="video/*"
              style="display: none"
            />
            <div class="upload-placeholder" @click="$refs.video1Input.click()">
              <i class="fas fa-cloud-upload-alt"></i>
              <p>Click to upload Video 1</p>
            </div>
            <div v-if="uploadProgress[1] > 0" class="upload-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: uploadProgress[1] + '%' }"></div>
              </div>
              <p>{{ uploadProgress[1] }}% uploaded</p>
            </div>
          </div>
          <div v-else class="video-uploaded">
            <video 
              :ref="`video1Player`"
              :src="getVideoUrl(currentComparison[`video1_cdn_url`] || currentComparison[`video1`])"
              class="uploaded-video"
              @loadedmetadata="onIndividualVideoLoaded(1)"
              @timeupdate="onVideoTimeUpdate(1)"
              :playsinline="true"
              :controls="false"
              @click.prevent="handleVideoClick(1)"
            ></video>
            
            <!-- Individual Video Controls for Video 1 -->
            <div v-if="!syncConfirmed" class="individual-video-controls">
              <button @click="toggleIndividualPlay(1)" class="individual-control-button">
                <i :class="individualVideoStates[1]?.isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
              </button>
              
              <div class="individual-time-display">
                {{ formatTime(individualVideoStates[1]?.currentTime || 0) }}
              </div>
              
              <div class="individual-progress-container">
                <input 
                  type="range"
                  min="0"
                  :max="individualVideoStates[1]?.duration || 0"
                  step="0.1"
                  :value="individualVideoStates[1]?.currentTime || 0"
                  @input="seekIndividualVideo(1, $event.target.value)"
                  @click="console.log('Slider 1 clicked')"
                  @change="console.log('Slider 1 changed')"
                  class="individual-progress-slider"
                  :disabled="!individualVideoStates[1]?.duration"
                />
              </div>
              
              <button @click="setCurrentTimeAsGate(1)" class="individual-control-button set-gate">
                Set Gate
              </button>
            </div>
          </div>
          <div v-if="!syncConfirmed" class="gate-time-input">
            <label>Gate Time (seconds):</label>
            <input 
              v-model.number="currentComparison.video1_gate_time"
              type="number" 
              step="0.001"
              placeholder="0.000"
              class="form-input small"
              @input="updateGateTime(1, $event.target.value)"
            />
          </div>
        </div>

        <div class="video-upload-card">
          <h4 v-if="!syncConfirmed">Video 2</h4>
          <div v-if="!currentComparison.video2" class="upload-area">
            <input 
              type="file" 
              ref="video2Input"
              @change="handleVideoSelect($event, 2)"
              accept="video/*"
              style="display: none"
            />
            <div class="upload-placeholder" @click="$refs.video2Input.click()">
              <i class="fas fa-cloud-upload-alt"></i>
              <p>Click to upload Video 2</p>
            </div>
            <div v-if="uploadProgress[2] > 0" class="upload-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: uploadProgress[2] + '%' }"></div>
              </div>
              <p>{{ uploadProgress[2] }}% uploaded</p>
            </div>
          </div>
          <div v-else class="video-uploaded">
            <video 
              :ref="`video2Player`"
              :src="getVideoUrl(currentComparison[`video2_cdn_url`] || currentComparison[`video2`])"
              class="uploaded-video"
              @loadedmetadata="onIndividualVideoLoaded(2)"
              @timeupdate="onVideoTimeUpdate(2)"
              :playsinline="true"
              :controls="false"
              @click.prevent="handleVideoClick(2)"
            ></video>
            
            <!-- Individual Video Controls for Video 2 -->
            <div v-if="!syncConfirmed" class="individual-video-controls">
              <button @click="toggleIndividualPlay(2)" class="individual-control-button">
                <i :class="individualVideoStates[2]?.isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
              </button>
              
              <div class="individual-time-display">
                {{ formatTime(individualVideoStates[2]?.currentTime || 0) }}
              </div>
              
              <div class="individual-progress-container">
                <input 
                  type="range"
                  min="0"
                  :max="individualVideoStates[2]?.duration || 0"
                  step="0.1"
                  :value="individualVideoStates[2]?.currentTime || 0"
                  @input="seekIndividualVideo(2, $event.target.value)"
                  @click="console.log('Slider 2 clicked')"
                  @change="console.log('Slider 2 changed')"
                  class="individual-progress-slider"
                  :disabled="!individualVideoStates[2]?.duration"
                />
              </div>
              
              <button @click="setCurrentTimeAsGate(2)" class="individual-control-button set-gate">
                Set Gate
              </button>
            </div>
          </div>
          <div v-if="!syncConfirmed" class="gate-time-input">
            <label>Gate Time (seconds):</label>
            <input 
              v-model.number="currentComparison.video2_gate_time"
              type="number" 
              step="0.001"
              placeholder="0.000"
              class="form-input small"
              @input="updateGateTime(2, $event.target.value)"
            />
          </div>
        </div>
      </div>

      <!-- Gate Time Confirmation Section -->
      <div v-if="currentComparison.video1 && currentComparison.video2 && !syncConfirmed" class="sync-confirmation-section">
        <div class="confirmation-card">
          <h4>Ready to Sync Videos?</h4>
          <p>Make sure you've set the correct gate times for both videos. You can watch each video independently above to find the exact moment the ski touches the gate.</p>
          
          <div class="gate-time-summary">
            <div class="gate-time-item">
              <strong>Video 1 Gate Time:</strong> 
              <span v-if="currentComparison.video1_gate_time">{{ currentComparison.video1_gate_time }} seconds</span>
              <span v-else class="not-set">Not set</span>
            </div>
            <div class="gate-time-item">
              <strong>Video 2 Gate Time:</strong> 
              <span v-if="currentComparison.video2_gate_time">{{ currentComparison.video2_gate_time }} seconds</span>
              <span v-else class="not-set">Not set</span>
            </div>
          </div>

          <div class="confirmation-actions">
            <button 
              @click="confirmSync" 
              class="action-button primary"
              :disabled="!currentComparison.video1_gate_time || !currentComparison.video2_gate_time"
            >
              Confirm & Sync Videos
            </button>
          </div>
        </div>
      </div>

      <!-- Synchronized Controls Section -->
      <div v-if="currentComparison.video1 && currentComparison.video2 && syncConfirmed" class="controls-section">
        <!-- Loading indicator when videos are not ready -->
        <div v-if="!videosLoaded[1] || !videosLoaded[2]" class="loading-indicator">
          <div class="loading-spinner"></div>
          <div class="loading-status">
            <span :class="{ 'loaded': videosLoaded[1] }">Video 1: {{ videosLoaded[1] ? 'Ready' : 'Loading...' }}</span>
            <span :class="{ 'loaded': videosLoaded[2] }">Video 2: {{ videosLoaded[2] ? 'Ready' : 'Loading...' }}</span>
          </div>
        </div>
        
        <div class="video-controls" :class="{ 'disabled': !videosLoaded[1] || !videosLoaded[2] }">
          <button @click="togglePlay" class="control-button play-pause" :disabled="!videosLoaded[1] || !videosLoaded[2]">
            <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
          </button>
          
          <div class="time-display">
            {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
          </div>
          
          <div class="progress-container">
            <input 
              type="range"
              min="0"
              :max="duration"
              step="0.1"
              v-model="currentTime"
              @input="seekTo"
              class="progress-slider"
              :disabled="!videosLoaded[1] || !videosLoaded[2]"
            />
          </div>
          
          <div class="speed-control">
            <label>Speed:</label>
            <select v-model="playbackRate" @change="setPlaybackRate" class="speed-select" :disabled="!videosLoaded[1] || !videosLoaded[2]">
              <option value="0.1">0.1x</option>
              <option value="0.25">0.25x</option>
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </select>
          </div>
        </div>

        <div class="sync-info" v-if="syncInstructions">
          <p><strong>Sync Info:</strong> 
            <span v-if="syncInstructions.delay_video">
              Video {{ syncInstructions.delay_video }} will be delayed by {{ syncInstructions.delay_seconds.toFixed(3) }} seconds
            </span>
            <span v-else>Videos are already in sync</span>
          </p>
          <button @click="resetSync" class="action-button secondary small">Adjust Gate Times</button>
        </div>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CompareMode',
  data() {
    return {
      loading: false,
      comparisons: [],
      currentComparison: null,
      newComparisonName: '',
      uploadProgress: { 1: 0, 2: 0 },
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      playbackRate: 1,
      syncInstructions: null,
      videosLoaded: { 1: false, 2: false },
      syncConfirmed: false,
      individualVideoStates: { 
        1: { isPlaying: false, currentTime: 0, duration: 0 }, 
        2: { isPlaying: false, currentTime: 0, duration: 0 } 
      }
    };
  },
  async created() {
    await this.fetchComparisons();
  },
  methods: {
    async fetchComparisons() {
      this.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get('https://api.6buoys.com/api/comparisons/', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        this.comparisons = response.data;
      } catch (error) {
        console.error('Error fetching comparisons:', error);
        alert('Failed to load comparisons');
      } finally {
        this.loading = false;
      }
    },

    async createComparison() {
      if (!this.newComparisonName.trim()) return;
      
      this.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.post('https://api.6buoys.com/api/comparisons/create/', {
          name: this.newComparisonName
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        this.currentComparison = response.data;
        this.newComparisonName = '';
        await this.fetchComparisons();
      } catch (error) {
        console.error('Error creating comparison:', error);
        alert('Failed to create comparison');
      } finally {
        this.loading = false;
      }
    },

    selectComparison(comparison) {
      this.currentComparison = comparison;
      this.syncInstructions = comparison.sync_instructions;
      
      // Auto-sync if both gate times are already set
      if (comparison.video1_gate_time && comparison.video2_gate_time) {
        this.syncConfirmed = true;
      } else {
        this.syncConfirmed = false;
      }
      
      // Initialize individual video states for existing videos
      this.$nextTick(() => {
        const video1 = this.$refs.video1Player;
        const video2 = this.$refs.video2Player;
        
        if (video1 && video1.duration) {
          this.individualVideoStates[1] = {
            isPlaying: false,
            currentTime: video1.currentTime || 0,
            duration: video1.duration
          };
        }
        
        if (video2 && video2.duration) {
          this.individualVideoStates[2] = {
            isPlaying: false,
            currentTime: video2.currentTime || 0,
            duration: video2.duration
          };
        }
        
        console.log('Selected comparison - individual video states:', this.individualVideoStates);
      });
    },

    backToList() {
      this.currentComparison = null;
      this.syncInstructions = null;
      this.syncConfirmed = false;
      this.resetVideoPlayers();
      
      // Refresh the comparisons list to show any updates
      this.fetchComparisons();
    },

    async deleteComparison(id) {
      if (!confirm('Are you sure you want to delete this comparison?')) return;
      
      try {
        const token = localStorage.getItem('access_token');
        await axios.delete(`https://api.6buoys.com/api/comparisons/${id}/delete/`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        await this.fetchComparisons();
        if (this.currentComparison && this.currentComparison.id === id) {
          this.currentComparison = null;
        }
      } catch (error) {
        console.error('Error deleting comparison:', error);
        alert('Failed to delete comparison');
      }
    },

    async handleVideoSelect(event, videoNumber) {
      const file = event.target.files[0];
      if (!file) return;

      await this.uploadVideo(file, videoNumber);
    },

    async uploadVideo(file, videoNumber) {
      const chunkSize = 1024 * 1024; // 1MB chunks
      const totalChunks = Math.ceil(file.size / chunkSize);
      let uploadId = '';

      this.uploadProgress[videoNumber] = 0;

      try {
        for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
          const start = (chunkNumber - 1) * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);

          const formData = new FormData();
          formData.append('chunk', chunk);
          formData.append('chunk_number', chunkNumber);
          formData.append('total_chunks', totalChunks);
          formData.append('filename', file.name);
          formData.append('upload_id', uploadId);
          formData.append('video_number', videoNumber);

          const token = localStorage.getItem('access_token');
          const response = await axios.post('https://api.6buoys.com/api/comparisons/upload-chunk/', formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });

          if (chunkNumber === 1) {
            uploadId = response.data.upload_id;
          }

          this.uploadProgress[videoNumber] = Math.round((chunkNumber / totalChunks) * 100);

          if (response.data.status === 'complete') {
            // Attach video to comparison
            await this.attachVideoToComparison(response.data.video_path, videoNumber);
            break;
          }
        }
      } catch (error) {
        console.error('Error uploading video:', error);
        alert('Failed to upload video');
        this.uploadProgress[videoNumber] = 0;
      }
    },

    async attachVideoToComparison(videoPath, videoNumber) {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.post(
          `https://api.6buoys.com/api/comparisons/${this.currentComparison.id}/attach-video/`,
          {
            video_path: videoPath,
            video_number: videoNumber.toString()
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        this.currentComparison = response.data;
        this.syncInstructions = response.data.sync_instructions;
      } catch (error) {
        console.error('Error attaching video:', error);
        alert('Failed to attach video to comparison');
      }
    },

    async updateGateTime(videoNumber, value) {
      if (!this.currentComparison) return;
      
      try {
        const token = localStorage.getItem('access_token');
        const updateData = {};
        
        if (videoNumber === 1) {
          updateData.video1_gate_time = value || null;
        } else {
          updateData.video2_gate_time = value || null;
        }

        const response = await axios.patch(
          `https://api.6buoys.com/api/comparisons/${this.currentComparison.id}/update/`,
          updateData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        this.currentComparison = response.data;
        
        // Always update sync instructions when gate times change
        this.syncInstructions = response.data.sync_instructions;
        
        console.log('Gate time updated, new sync instructions:', this.syncInstructions);
      } catch (error) {
        console.error('Error updating gate time:', error);
      }
    },

    getVideoUrl(url) {
      if (!url) return '';
      // If it's already a full URL, return as is
      if (url.startsWith('http')) return url;
      // Otherwise, assume it's a relative path from S3
      return `https://api.6buoys.com${url}`;
    },

    onIndividualVideoLoaded(videoNumber) {
      console.log(`Video ${videoNumber} loaded`);
      this.videosLoaded[videoNumber] = true;
      
      // Set duration for individual video controls
      const video = this.$refs[`video${videoNumber}Player`];
      if (video) {
        this.individualVideoStates[videoNumber].duration = video.duration;
        
        // Check if we're on mobile
        const isMobile = window.innerWidth <= 768;
        
        // Adjust container height based on video aspect ratio to minimize black space
        const aspectRatio = video.videoWidth / video.videoHeight;
        const containerWidth = video.parentElement.clientWidth;
        const idealHeight = containerWidth / aspectRatio;
        
        // Set different max heights based on screen size and sync status
        let maxHeight;
        if (isMobile) {
          // Much larger video sizes for mobile to better use screen space
          const screenHeight = window.innerHeight;
          const availableHeight = screenHeight - 200; // Leave space for controls and header
          
          if (this.syncConfirmed) {
            // When synced, use more of the screen height
            maxHeight = Math.min(availableHeight * 0.4, window.innerWidth <= 480 ? 280 : 320);
          } else {
            // When not synced, even larger since there's only one video at a time in view
            maxHeight = Math.min(availableHeight * 0.6, window.innerWidth <= 480 ? 400 : 450);
          }
          
          // For vertical videos (aspect ratio < 1), allow even more height
          if (aspectRatio < 1) {
            maxHeight = Math.min(availableHeight * (this.syncConfirmed ? 0.5 : 0.7), 500);
          }
        } else {
          maxHeight = this.syncConfirmed ? 500 : 380; // Desktop sizes
        }
        
        if (idealHeight <= maxHeight) {
          video.style.height = idealHeight + 'px';
          video.style.width = '100%';
        } else {
          video.style.height = maxHeight + 'px';
          video.style.width = (maxHeight * aspectRatio) + 'px';
          // Center the video if it's narrower than the container
          if ((maxHeight * aspectRatio) < containerWidth) {
            video.style.margin = '0 auto';
            video.style.display = 'block';
          }
        }
      }
      
      // Check if both videos are now loaded for synchronized playback
      if (this.videosLoaded[1] && this.videosLoaded[2]) {
        console.log('Both videos loaded - synchronized playback ready');
        // Both videos loaded, set duration to the shorter one for sync controls
        const video1 = this.$refs.video1Player;
        const video2 = this.$refs.video2Player;
        this.duration = Math.min(video1.duration, video2.duration);
        
        // If we were trying to play but waiting for loading, we can now proceed
        if (this.syncConfirmed && this.isPlaying) {
          console.log('Resuming playback now that both videos are loaded');
          this.playActiveVideos();
        }
      }
    },

    onVideoTimeUpdate(videoNumber) {
      const video = this.$refs[`video${videoNumber}Player`];
      if (!video) return;
      
      // Update individual video state
      if (this.individualVideoStates[videoNumber]) {
        this.individualVideoStates[videoNumber].currentTime = video.currentTime;
      }
      
      // Update synced currentTime if we're in sync mode and playing
      if (this.syncConfirmed && this.isPlaying) {
        // Use the reference video (the one without delay) for time tracking
        if (this.syncInstructions && this.syncInstructions.delay_video === 1 && videoNumber === 2) {
          this.currentTime = video.currentTime;
        } else if ((!this.syncInstructions || this.syncInstructions.delay_video !== 1) && videoNumber === 1) {
          this.currentTime = video.currentTime;
        }
        
        // Check if delayed video should start playing
        if (this.syncInstructions && this.syncInstructions.delay_video) {
          const delaySeconds = this.syncInstructions.delay_seconds;
          const video1 = this.$refs.video1Player;
          const video2 = this.$refs.video2Player;
          
          if (this.syncInstructions.delay_video === 1 && this.currentTime >= delaySeconds) {
            // Video 1 should start if it's not already playing
            if (video1 && video1.paused) {
              video1.currentTime = this.currentTime - delaySeconds;
              video1.play();
            }
          } else if (this.syncInstructions.delay_video === 2 && this.currentTime >= delaySeconds) {
            // Video 2 should start if it's not already playing
            if (video2 && video2.paused) {
              video2.currentTime = this.currentTime - delaySeconds;
              video2.play();
            }
          }
        }
      }
    },

    toggleIndividualPlay(videoNumber) {
      const video = this.$refs[`video${videoNumber}Player`];
      if (video) {
        if (this.individualVideoStates[videoNumber].isPlaying) {
          video.pause();
        } else {
          video.play();
        }
        this.individualVideoStates[videoNumber].isPlaying = !this.individualVideoStates[videoNumber].isPlaying;
      }
    },

    seekIndividualVideo(videoNumber, value) {
      console.log(`SeekIndividualVideo called: video ${videoNumber}, value: ${value}`);
      const video = this.$refs[`video${videoNumber}Player`];
      console.log(`Video ref found:`, video);
      
      if (video) {
        const time = parseFloat(value);
        console.log(`Setting video ${videoNumber} currentTime to ${time}`);
        video.currentTime = time;
        
        // Ensure the state is updated
        if (!this.individualVideoStates[videoNumber]) {
          this.individualVideoStates[videoNumber] = { isPlaying: false, currentTime: 0, duration: 0 };
        }
        this.individualVideoStates[videoNumber].currentTime = time;
        console.log(`Updated state for video ${videoNumber}:`, this.individualVideoStates[videoNumber]);
      } else {
        console.log(`Video ${videoNumber} ref not found`);
        console.log('Available refs:', Object.keys(this.$refs));
      }
    },

    setCurrentTimeAsGate(videoNumber) {
      if (this.currentComparison) {
        const video = this.$refs[`video${videoNumber}Player`];
        if (video) {
          const currentTime = video.currentTime.toFixed(3);
          // Update the gate time input field and trigger the update
          if (videoNumber === 1) {
            this.currentComparison.video1_gate_time = currentTime;
          } else {
            this.currentComparison.video2_gate_time = currentTime;
          }
          // Call the existing update method
          this.updateGateTime(videoNumber, currentTime);
        }
      }
    },

    togglePlay() {
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      if (!video1 || !video2) return;

      // Check if both videos are fully loaded before allowing synchronized playback
      if (!this.videosLoaded[1] || !this.videosLoaded[2]) {
        console.log('Waiting for both videos to load before synchronized playback');
        return;
      }

      if (this.isPlaying) {
        // Pause both videos simultaneously
        video1.pause();
        video2.pause();
        this.isPlaying = false;
      } else {
        // Check if we need to apply sync (first play or after seeking)
        const needsSync = Math.abs(video1.currentTime - this.getExpectedVideo1Time()) > 0.1 || 
                         Math.abs(video2.currentTime - this.getExpectedVideo2Time()) > 0.1;
        
        if (needsSync) {
          // Apply sync offset first
          this.seekTo();
          
          // Small delay for seek completion, then play active videos
          setTimeout(() => {
            this.playActiveVideos();
          }, 50);
        } else {
          // Resume immediately from current positions
          this.playActiveVideos();
        }
        
        this.isPlaying = true;
      }
    },

    getExpectedVideo1Time() {
      if (!this.syncInstructions || !this.syncInstructions.delay_video) {
        return this.currentTime;
      }
      
      if (this.syncInstructions.delay_video === 1) {
        return Math.max(0, this.currentTime - this.syncInstructions.delay_seconds);
      } else {
        return this.currentTime;
      }
    },

    getExpectedVideo2Time() {
      if (!this.syncInstructions || !this.syncInstructions.delay_video) {
        return this.currentTime;
      }
      
      if (this.syncInstructions.delay_video === 2) {
        return Math.max(0, this.currentTime - this.syncInstructions.delay_seconds);
      } else {
        return this.currentTime;
      }
    },

    playActiveVideos() {
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      // Ensure both videos are loaded before attempting synchronized playback
      if (!this.videosLoaded[1] || !this.videosLoaded[2]) {
        console.log('Cannot play - waiting for both videos to load');
        return;
      }
      
      if (this.syncInstructions && this.syncInstructions.delay_video) {
        const delaySeconds = this.syncInstructions.delay_seconds;
        
        if (this.syncInstructions.delay_video === 1) {
          // Video 1 is delayed - only play if timeline is past delay
          if (this.currentTime >= delaySeconds) {
            video1.play().catch(e => console.log('Video 1 play failed:', e));
          }
          video2.play().catch(e => console.log('Video 2 play failed:', e)); // Video 2 always plays
        } else {
          // Video 2 is delayed - only play if timeline is past delay
          video1.play().catch(e => console.log('Video 1 play failed:', e)); // Video 1 always plays
          if (this.currentTime >= delaySeconds) {
            video2.play().catch(e => console.log('Video 2 play failed:', e));
          }
        }
      } else {
        // No delay, play both
        video1.play().catch(e => console.log('Video 1 play failed:', e));
        video2.play().catch(e => console.log('Video 2 play failed:', e));
      }
    },

    seekTo() {
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      if (!video1 || !video2) return;
      
      // Ensure both videos are loaded before seeking
      if (!this.videosLoaded[1] || !this.videosLoaded[2]) {
        console.log('Cannot seek - waiting for both videos to load');
        return;
      }

      if (this.syncInstructions && this.syncInstructions.delay_video) {
        const delaySeconds = this.syncInstructions.delay_seconds;
        
        if (this.syncInstructions.delay_video === 1) {
          // Video 1 is delayed
          const video1StartTime = this.currentTime - delaySeconds;
          
          if (video1StartTime >= 0) {
            // Video 1 should be playing
            video1.currentTime = video1StartTime;
            if (this.isPlaying) {
              video1.play().catch(e => console.log('Video 1 play failed:', e));
            }
          } else {
            // Video 1 should not be playing yet
            video1.currentTime = 0;
            video1.pause();
          }
          
          // Video 2 always plays normally
          video2.currentTime = this.currentTime;
          
        } else {
          // Video 2 is delayed
          const video2StartTime = this.currentTime - delaySeconds;
          
          if (video2StartTime >= 0) {
            // Video 2 should be playing
            video2.currentTime = video2StartTime;
            if (this.isPlaying) {
              video2.play().catch(e => console.log('Video 2 play failed:', e));
            }
          } else {
            // Video 2 should not be playing yet
            video2.currentTime = 0;
            video2.pause();
          }
          
          // Video 1 always plays normally
          video1.currentTime = this.currentTime;
        }
      } else {
        // No sync needed, both play normally
        video1.currentTime = this.currentTime;
        video2.currentTime = this.currentTime;
      }
    },

    setPlaybackRate() {
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      if (video1 && video2) {
        video1.playbackRate = this.playbackRate;
        video2.playbackRate = this.playbackRate;
      }
    },

    resetVideoPlayers() {
      this.isPlaying = false;
      this.currentTime = 0;
      this.duration = 0;
      this.playbackRate = 1;
      this.videosLoaded = { 1: false, 2: false };
      this.syncConfirmed = false;
      this.individualVideoStates = { 1: { isPlaying: false, currentTime: 0, duration: 0 }, 2: { isPlaying: false, currentTime: 0, duration: 0 } };
    },

    formatTime(seconds) {
      if (isNaN(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    confirmSync() {
      this.syncConfirmed = true;
      // Calculate sync instructions now
      if (this.currentComparison && this.currentComparison.sync_instructions) {
        this.syncInstructions = this.currentComparison.sync_instructions;
      }
      
      // Re-trigger video sizing for larger display when synced
      this.$nextTick(() => {
        if (this.videosLoaded[1]) {
          this.onIndividualVideoLoaded(1);
        }
        if (this.videosLoaded[2]) {
          this.onIndividualVideoLoaded(2);
        }
      });
    },

    resetSync() {
      this.syncConfirmed = false;
      this.syncInstructions = null;
      
      // Reset synchronized controls
      this.isPlaying = false;
      this.currentTime = 0;
      this.playbackRate = 1;
      
      // Reinitialize individual video states for existing videos
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      if (video1) {
        this.individualVideoStates[1] = {
          isPlaying: false,
          currentTime: video1.currentTime || 0,
          duration: video1.duration || 0
        };
        video1.pause();
      }
      
      if (video2) {
        this.individualVideoStates[2] = {
          isPlaying: false,
          currentTime: video2.currentTime || 0,
          duration: video2.duration || 0
        };
        video2.pause();
      }
      
      console.log('Reset sync - individual video states:', this.individualVideoStates);
      
      // Re-trigger video sizing for smaller display when not synced
      this.$nextTick(() => {
        if (this.videosLoaded[1]) {
          this.onIndividualVideoLoaded(1);
        }
        if (this.videosLoaded[2]) {
          this.onIndividualVideoLoaded(2);
        }
      });
    },

    handleVideoClick(videoNumber) {
      // Prevent default fullscreen behavior and toggle play instead
      this.toggleIndividualPlay(videoNumber);
    }
  }
};
</script>

<style scoped>
.compare-mode-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-bottom: 80px;
  color: #ffffff;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
  position: relative;
}

.page-header h2 {
  color: #1da1f2;
  font-size: 2rem;
  margin-bottom: 10px;
}

.page-header p {
  color: #8899a6;
  font-size: 1.1rem;
}

.feature-credit {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 0.75rem;
  color: #657786;
  opacity: 0.7;
  font-style: italic;
}

.create-comparison {
  background-color: #192734;
  border-radius: 16px;
  padding: 25px;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #ffffff;
  font-weight: 500;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #38444d;
  border-radius: 8px;
  background-color: #15202b;
  color: #ffffff;
  font-size: 16px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #1da1f2;
}

.form-input.small {
  width: auto;
  min-width: 120px;
}

.action-button {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button.primary {
  background-color: #1da1f2;
  color: white;
}

.action-button.primary:hover {
  background-color: #1991db;
}

.action-button.secondary {
  background-color: #8899a6;
  color: white;
}

.action-button.secondary:hover {
  background-color: #7a8a97;
}

.action-button.danger {
  background-color: #e1306c;
  color: white;
}

.action-button.danger:hover {
  background-color: #c92a5d;
}

.action-button:disabled {
  background-color: #38444d;
  color: #657786;
  cursor: not-allowed;
}

.action-button.small {
  padding: 6px 12px;
  font-size: 14px;
  margin-left: 15px;
}

.comparisons-list {
  margin-bottom: 30px;
}

.comparisons-list h3 {
  color: #ffffff;
  margin-bottom: 20px;
  font-size: 1.5rem;
}

.comparison-card {
  background-color: #192734;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.comparison-info h4 {
  color: #ffffff;
  margin-bottom: 8px;
  font-size: 1.2rem;
  text-align: left;
}

.comparison-info p {
  color: #8899a6;
  margin-bottom: 10px;
  text-align: left;
}

.video-status {
  display: flex;
  gap: 10px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  background-color: #38444d;
  color: #8899a6;
}

.status-badge.complete {
  background-color: #1d9bf0;
  color: white;
}

.comparison-actions {
  display: flex;
  gap: 10px;
}

.comparison-interface {
  background-color: #192734;
  border-radius: 16px;
  padding: 25px;
}

.comparison-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid #38444d;
}

.comparison-header h3 {
  color: #ffffff;
  font-size: 1.5rem;
}

.video-upload-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.video-upload-card {
  background-color: #15202b;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.video-upload-card h4 {
  color: #1da1f2;
  margin-bottom: 15px;
  text-align: center;
  flex-shrink: 0;
}

.upload-area {
  margin-bottom: 20px;
}

.upload-placeholder {
  border: 2px dashed #38444d;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.upload-placeholder:hover {
  border-color: #1da1f2;
}

.upload-placeholder i {
  font-size: 2rem;
  color: #8899a6;
  margin-bottom: 10px;
}

.upload-placeholder p {
  color: #8899a6;
  margin: 0;
}

.upload-progress {
  margin-top: 15px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #38444d;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill {
  height: 100%;
  background-color: #1da1f2;
  transition: width 0.2s ease;
}

.video-uploaded {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px;
  background-color: #15202b;
  border-radius: 12px;
  min-height: 150px;
}

/* Larger videos when synced */
.comparison-interface[data-synced="true"] .uploaded-video {
  max-height: 550px;
}

.comparison-interface[data-synced="true"] .video-uploaded {
  padding: 10px;
}

.comparison-interface[data-synced="true"] .video-upload-card {
  padding: 15px;
}

.video-uploaded .uploaded-video {
  margin-bottom: 15px;
}

.video-uploaded i {
  font-size: 2rem;
  margin-bottom: 10px;
}

.uploaded-video {
  max-width: 100%;
  height: auto;
  object-fit: contain;
  background-color: #000;
  border-radius: 8px;
  display: block;
  margin: 0 auto;
}

.gate-time-input {
  display: flex;
  align-items: center;
  gap: 10px;
}

.gate-time-input label {
  color: #ffffff;
  font-size: 0.9rem;
  margin-bottom: 0;
}

.controls-section {
  margin-top: 30px;
}

.video-controls {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background-color: #15202b;
  border-radius: 12px;
}

.sync-info {
  background-color: #15202b;
  border-radius: 8px;
  padding: 15px;
  margin-top: 20px;
  text-align: center;
}

.sync-info p {
  margin: 0;
  color: #1da1f2;
}

.control-button {
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background-color: #1da1f2;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.control-button:hover {
  background-color: #1991db;
}

.time-display {
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
}

.progress-container {
  flex: 1;
}

.progress-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #38444d;
  outline: none;
  cursor: pointer;
}

.progress-slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #1da1f2;
  cursor: pointer;
}

.speed-control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.speed-control label {
  color: #ffffff;
  font-size: 0.9rem;
}

.speed-select {
  padding: 8px 12px;
  border: 1px solid #38444d;
  border-radius: 6px;
  background-color: #15202b;
  color: #ffffff;
  font-size: 0.9rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(21, 32, 43, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #38444d;
  border-top: 4px solid #1da1f2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-overlay p {
  color: #ffffff;
  font-size: 1.1rem;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .compare-mode-container {
    padding: 10px;
  }

  .page-header {
    margin-bottom: 20px;
  }

  .page-header h2 {
    font-size: 1.5rem;
    margin-bottom: 5px;
  }

  .page-header p {
    font-size: 1rem;
    margin-bottom: 10px;
  }

  .feature-credit {
    position: static;
    text-align: center;
    margin-top: 5px;
    font-size: 0.7rem;
  }

  /* Keep videos side-by-side on mobile when synced, stack when not synced */
  .video-upload-section {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 20px;
  }

  .comparison-interface[data-synced="true"] .video-upload-section {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .video-upload-card {
    padding: 10px;
  }

  .comparison-interface[data-synced="true"] .video-upload-card {
    padding: 8px;
  }

  .video-upload-card h4 {
    font-size: 0.9rem;
    margin-bottom: 8px;
  }

  /* Optimize video container for larger videos */
  .video-uploaded {
    padding: 8px;
    background-color: #15202b;
    border-radius: 8px;
    min-height: auto;
  }

  .comparison-interface[data-synced="true"] .video-uploaded {
    padding: 5px;
  }

  /* Video sizing for mobile - much larger */
  .uploaded-video {
    max-height: 450px;
    max-width: 100%;
    width: 100%;
    object-fit: contain;
    border-radius: 6px;
  }

  /* Larger videos when synced to better use screen space */
  .comparison-interface[data-synced="true"] .uploaded-video {
    max-height: 320px;
  }

  /* Mobile-friendly individual video controls */
  .individual-video-controls {
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
    background: #192734;
    border-radius: 8px;
    margin-top: 8px;
  }

  .individual-control-button {
    width: 40px;
    height: 40px;
    font-size: 0.8rem;
    min-touch-target: 44px;
  }

  .individual-control-button.set-gate {
    min-width: 75px;
    padding: 8px 10px;
    font-size: 0.7rem;
    height: 40px;
    margin-left: auto;
  }

  .individual-time-display {
    font-size: 0.8rem;
    padding: 6px 8px;
    background-color: rgba(21, 32, 43, 0.8);
  }

  .individual-progress-container {
    flex-basis: 100%;
    width: 100%;
    margin: 8px 0 0 0;
    order: 10;
  }

  .individual-progress-slider {
    height: 10px;
    border-radius: 5px;
  }

  .individual-progress-slider:hover {
    height: 12px;
  }

  .individual-progress-slider::-webkit-slider-thumb {
    width: 26px;
    height: 26px;
  }

  /* Gate time inputs */
  .gate-time-input {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    margin-top: 8px;
  }

  .gate-time-input label {
    font-size: 0.8rem;
  }

  .form-input.small {
    width: 100%;
    min-width: unset;
    font-size: 16px;
    padding: 10px 12px;
  }

  /* Synchronized controls for mobile */
  .video-controls {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px;
    margin-top: 15px;
  }

  .control-button {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }

  .time-display {
    font-size: 0.9rem;
    order: -1;
    flex-basis: 100%;
    text-align: center;
    padding: 8px;
    background-color: rgba(21, 32, 43, 0.6);
    border-radius: 6px;
    margin-bottom: 6px;
  }

  .progress-container {
    order: 2;
    flex-basis: 100%;
    margin: 6px 0;
  }

  .progress-slider {
    height: 8px;
  }

  .progress-slider::-webkit-slider-thumb {
    width: 24px;
    height: 24px;
  }

  .speed-control {
    order: 3;
    justify-content: center;
    flex-basis: 100%;
  }

  .speed-select {
    font-size: 16px;
    padding: 10px 15px;
    min-width: 80px;
  }

  /* Comparison cards */
  .comparison-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
  }

  .comparison-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .action-button {
    padding: 12px 20px;
    font-size: 16px;
    min-height: 44px;
  }

  /* Sync confirmation */
  .sync-confirmation-section {
    margin-top: 20px;
    padding: 15px;
  }

  .confirmation-card {
    padding: 12px;
  }

  .confirmation-card h4 {
    font-size: 1.2rem;
  }

  .gate-time-summary {
    text-align: left;
    margin: 12px 0;
  }

  .gate-time-item {
    margin-bottom: 6px;
    font-size: 0.85rem;
  }

  /* Upload areas */
  .upload-placeholder {
    padding: 25px 12px;
  }

  .upload-placeholder i {
    font-size: 1.4rem;
  }

  .upload-placeholder p {
    font-size: 0.85rem;
  }

  /* Form inputs */
  .form-input {
    font-size: 16px;
    padding: 12px 16px;
  }

  /* Sync info */
  .sync-info {
    padding: 10px;
    text-align: left;
    margin-top: 15px;
  }

  .sync-info p {
    font-size: 0.85rem;
    margin-bottom: 8px;
  }

  .action-button.small {
    padding: 6px 12px;
    font-size: 13px;
    margin-left: 0;
    margin-top: 6px;
  }

  /* Reduce header spacing */
  .comparison-header {
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  .comparison-header h3 {
    font-size: 1.3rem;
  }

  /* Optimize comparison interface padding */
  .comparison-interface {
    padding: 15px;
  }

  /* Loading indicator styles */
  .loading-indicator {
    text-align: center;
    padding: 20px;
    background-color: #15202b;
    border-radius: 12px;
    margin-bottom: 20px;
  }

  .loading-spinner {
    width: 30px;
    height: 30px;
    border: 3px solid #38444d;
    border-top: 3px solid #1da1f2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px auto;
  }

  .loading-indicator p {
    color: #8899a6;
    margin-bottom: 15px;
    font-size: 0.9rem;
  }

  .loading-status {
    display: flex;
    justify-content: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  .loading-status span {
    color: #e1306c;
    font-size: 0.8rem;
    padding: 4px 8px;
    border-radius: 4px;
    background-color: rgba(225, 48, 108, 0.1);
  }

  .loading-status span.loaded {
    color: #17bf63;
    background-color: rgba(23, 191, 99, 0.1);
  }

  /* Disabled controls styling */
  .video-controls.disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .control-button:disabled {
    background-color: #38444d;
    color: #657786;
    cursor: not-allowed;
  }

  .progress-slider:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .speed-select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: #657786;
  }
}

/* Extra small mobile devices */
@media (max-width: 480px) {
  .compare-mode-container {
    padding: 8px;
  }

  /* Even larger videos on small screens */
  .uploaded-video {
    max-height: 400px;
  }

  .comparison-interface[data-synced="true"] .uploaded-video {
    max-height: 280px;
  }

  .individual-control-button.set-gate {
    min-width: 65px;
    font-size: 0.65rem;
    margin-left: auto;
  }

  .video-upload-card {
    padding: 8px;
  }

  .comparison-interface[data-synced="true"] .video-upload-card {
    padding: 6px;
  }

  .individual-video-controls {
    padding: 6px 8px;
  }

  .comparison-interface {
    padding: 12px;
  }

  .comparison-interface[data-synced="true"] .video-upload-section {
    gap: 6px;
  }

  .individual-progress-slider {
    height: 12px;
    border-radius: 6px;
  }

  .individual-progress-slider:hover {
    height: 14px;
  }

  .individual-progress-slider::-webkit-slider-thumb {
    width: 28px;
    height: 28px;
  }
}

@media (min-width: 769px) {
  .video-upload-section {
    align-items: start;
  }
  
  .video-upload-card {
    height: fit-content;
  }
}

.sync-confirmation-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #15202b;
  border-radius: 16px;
}

.confirmation-card {
  background-color: #192734;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.confirmation-card h4 {
  color: #1da1f2;
  margin-bottom: 15px;
  font-size: 1.5rem;
}

.confirmation-card p {
  color: #8899a6;
  margin-bottom: 20px;
}

.gate-time-summary {
  margin-bottom: 20px;
}

.gate-time-item {
  margin-bottom: 10px;
}

.gate-time-item strong {
  color: #ffffff;
  font-weight: 500;
}

.gate-time-item span {
  color: #8899a6;
  font-weight: 400;
}

.gate-time-item .not-set {
  color: #e1306c;
}

.confirmation-actions {
  margin-top: 20px;
}

.individual-video-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #192734 0%, #1a2a39 100%);
  border-radius: 12px;
  border: 1px solid rgba(29, 161, 242, 0.1);
}

.individual-control-button {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 18px;
  background-color: #1da1f2;
  color: white;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 4px rgba(29, 161, 242, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.individual-control-button:hover {
  background-color: #1991db;
  transform: translateY(-0.5px);
  box-shadow: 0 2px 6px rgba(29, 161, 242, 0.3);
}

.individual-control-button.set-gate {
  width: auto;
  min-width: 90px;
  padding: 8px 14px;
  border-radius: 18px;
  font-size: 0.7rem;
  font-weight: 600;
  background-color: #17bf63;
  box-shadow: 0 1px 4px rgba(23, 191, 99, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  margin-left: auto;
}

.individual-control-button.set-gate:hover {
  background-color: #15a854;
  transform: translateY(-0.5px);
  box-shadow: 0 2px 6px rgba(23, 191, 99, 0.3);
}

.individual-time-display {
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  background-color: rgba(21, 32, 43, 0.6);
  padding: 6px 10px;
  border-radius: 6px;
}

.individual-progress-container {
  flex-basis: 100%;
  width: 100%;
  margin: 8px 0 0 0;
  order: 10;
}

.individual-progress-slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(90deg, #38444d 0%, #4a5568 100%);
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.individual-progress-slider:hover {
  height: 10px;
}

.individual-progress-slider::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1da1f2 0%, #1991db 100%);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(29, 161, 242, 0.4);
  transition: all 0.2s ease;
}

.individual-progress-slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(29, 161, 242, 0.6);
}

.individual-control-button i {
  font-size: 0.7rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.individual-control-button.set-gate i {
  font-size: 0.7rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.individual-control-button .fas.fa-pause {
    margin-top: 10px;
}

.individual-control-button .fas.fa-play {
    margin-top: 10px;
}

.individual-control-button .fas.fa-stop {
    margin-top: 10px;
}
</style> 